import { compose, pick } from 'ramda';
import React, { useEffect } from 'react';
import { Loader } from 'js/components/common/Loader';
import { Button } from 'js/components/common/Button';
import { useFormatter } from 'js/hooks/useFormatter';
import { componentService } from 'js/services/component.service';
import { withComponent } from 'js/components/hocs/withComponent';
import { useComponentConfig } from 'js/hooks/component/useComponentConfig';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';
import { usePushSubscription } from 'server';

import * as hooks from './hooks';
import { Form } from './components/Form';
import { Action } from './components/Action';
import { useDynamicReducerSelector, withDynamicReducer } from './store';
import { FUND_TRANSFER, FUND_TRANSFER_TYPE_DEPOSIT, FUND_TRANSFER_TYPE_WITHDRAW } from './constants';

import style from './FundTransfer.scss';

export const FundTransferBare = () => {
    const formatter = useFormatter();
    const translate = useComponentTranslations();
    const fetchExternalBalance = hooks.useFetchExternalBalance();
    const onSubmit = hooks.useOnSubmit({ fetchExternalBalance });
    const fetchInternalBalance = hooks.useFetchInternalBalance();
    const updateInternalBalance = hooks.useUpdateInternalBalance();
    const { externalDepositPage, internalDepositPage } = useComponentConfig();
    const { internalBalance, externalBalance } = useDynamicReducerSelector(pick(['internalBalance', 'externalBalance']));

    useEffect(() => {
        fetchExternalBalance();
        fetchInternalBalance();
    }, [
        fetchExternalBalance,
        fetchInternalBalance
    ]);

    usePushSubscription([32010], () => (push) => {
        updateInternalBalance(push);
    });

    if (!externalBalance || !internalBalance) {
        return <Loader />;
    }

    return (
        <div className={style.container}>
            <div className={style.title}>{translate('title')}</div>
            <div className={style.balances}>
                <div className={style.balanceBlock}>
                    <div className={style.balanceTitle}>{translate('balance.external.title')}</div>
                    <div className={style.balanceDescription}>{translate('balance.external.description')}</div>
                    <div className={style.balanceAmount}>
                        {externalBalance.message ?? formatter.formatCurrency(externalBalance.amount, {
                            currencyCode: externalBalance.currencyCode
                        })}
                    </div>
                    {
                        externalDepositPage
                            ? <div className={style.balanceButtonContainer}>
                                <Button className={style.balanceButton} href={externalDepositPage}>{translate('balance.external.button')}</Button>
                            </div>
                            : null
                    }
                </div>
                <div className={style.balanceBlock}>
                    <div className={style.balanceTitle}>{translate('balance.internal.title')}</div>
                    <div className={style.balanceDescription}>{translate('balance.internal.description')}</div>
                    <div className={style.balanceAmount}>
                        {internalBalance.message ?? formatter.formatCurrency(internalBalance.amount, {
                            currencyCode: internalBalance.currencyCode
                        })}
                    </div>
                    {
                        internalDepositPage
                            ? <div className={style.balanceButtonContainer}>
                                <Button className={style.balanceButton} href={internalDepositPage}>{translate('balance.internal.button')}</Button>
                            </div>
                            : null
                    }
                </div>
            </div>
            <div className={style.actions}>
                <Action title={translate('transfer.deposit')}>
                    <Form onSubmit={onSubmit} initialValues={{ type: FUND_TRANSFER_TYPE_DEPOSIT }} />
                </Action>
                <Action title={translate('transfer.withdraw')}>
                    <Form onSubmit={onSubmit} initialValues={{ type: FUND_TRANSFER_TYPE_WITHDRAW }} />
                </Action>
            </div>
        </div>
    );
};

export const FundTransfer = compose(
    withComponent({ isForLoggedIn: true }),
    withDynamicReducer()
)(FundTransferBare);

componentService.registerPageComponents({ [FUND_TRANSFER]: FundTransfer });
